"use client";

import React from "react";
import {
  Trophy,
  Medal,
  Star,
  Loader2,
  Users,
  Eye,
  Info,
  Search,
} from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";


import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/_module/components/ui/avatar";
import { cn } from "@/app/_module/lib/utils";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";

import { useGetAgentStats } from "../../hooks/use-hook";
import { agentStatsToAgencyUser } from "./AgencyAgentsPanel";
import type { AgentStats, AgencyUserWithAgency } from "../../types/types";

interface TopPerformersProps {
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string;
  endDate?: string;
  limit?: number;
  inTab?: boolean; // When true, renders without Card wrapper
  onViewAgentDetails?: (agencyUser: AgencyUserWithAgency) => void; // Callback when clicking on an agent
}

// `limit` is optional — when omitted, every agent in the agency tree shows
// (zero-stat ones sort to the bottom). Owners want to see everyone for
// audit/management purposes. Pass an explicit `limit` to truncate (e.g., a
// small "top 5" widget on a different surface).
export const TopPerformers = function({ period = '6-months', startDate, endDate, limit, inTab = false, onViewAgentDetails }: TopPerformersProps) {
  // Track current agency ID in state to force re-render on agency change
  const [currentAgencyId, setCurrentAgencyId] = React.useState<string | null>(
    AgencyTokenManager.getCurrentAgencyId()
  );

  // Listen for agency changes
  React.useEffect(() => {
    const handleAgencyChange = () => {
      const newAgencyId = AgencyTokenManager.getCurrentAgencyId();
      setCurrentAgencyId(newAgencyId);
    };

    window.addEventListener('insuracentral-agency-change', handleAgencyChange);
    return () => {
      window.removeEventListener('insuracentral-agency-change', handleAgencyChange);
    };
  }, []);

  const { data: agentStatsData, isLoading, error } = useGetAgentStats(currentAgencyId || '', period, startDate, endDate);

  // Sort agents by total premium descending. Slice only when an explicit
  // limit was passed; otherwise return the full list so every agent — even
  // zero-stat ones — appears.
  const topPerformers: AgentStats[] = React.useMemo(() => {
    if (!agentStatsData || !Array.isArray(agentStatsData)) return [];

    const sorted = [...agentStatsData].sort(
      (a, b) => (b.totalPremium || 0) - (a.totalPremium || 0),
    );
    return typeof limit === 'number' ? sorted.slice(0, limit) : sorted;
  }, [agentStatsData, limit]);

  // Shell: searchable by agent or agency name (list keeps its rank order —
  // ranks are the agent's TRUE position, filtering never renumbers them).
  const isShell =
    typeof window !== "undefined" && window.location.pathname.startsWith("/m");
  const [search, setSearch] = React.useState("");
  const q = search.trim().toLowerCase();
  const shownPerformers = q
    ? topPerformers.filter(
        (a) =>
          (a.name || "").toLowerCase().includes(q) ||
          ((a as { agencyName?: string }).agencyName || "").toLowerCase().includes(q),
      )
    : topPerformers;

  // Get rank badge styling
  const getRankBadge = (rank: number) => {
    switch (rank) {
      case 1:
        return (
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-700">
            <Trophy className="h-4 w-4" />
          </div>
        );
      case 2:
        return (
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600">
            <Medal className="h-4 w-4" />
          </div>
        );
      case 3:
        return (
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-700">
            <Medal className="h-4 w-4" />
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-semibold">
            {rank}
          </div>
        );
    }
  };

  // Loading content
  const loadingContent = (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  // Error content
  const errorContent = (
    <div className="flex items-center justify-center h-64 text-red-500">
      Failed to load top performers
    </div>
  );

  // Empty content
  const emptyContent = (
    <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
      <Users className="h-12 w-12 mb-4" />
      <p className="text-center">No agent data available yet</p>
      <p className="text-sm text-center mt-1">Agent performance will appear here once data is recorded</p>
    </div>
  );

  // Handle click on agent row
  const handleAgentClick = (agent: AgentStats) => {
    if (onViewAgentDetails) {
      onViewAgentDetails(agentStatsToAgencyUser(agent, currentAgencyId || ''));
    }
  };

  // Main content with performers list
  const performersContent = (
    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
      {isShell && topPerformers.length > 3 && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agents…"
            aria-label="Search top performers"
            className="h-10 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15"
          />
        </div>
      )}
      {isShell && q && shownPerformers.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No agents match &ldquo;{search.trim()}&rdquo;
        </p>
      )}
      {shownPerformers.map((agent) => {
        // TRUE rank — filtering hides rows, it never renumbers them.
        const index = topPerformers.indexOf(agent);
        return (
        <div
          key={agent.id}
          className={cn(
            "perf-row flex items-center gap-4 p-3 rounded-lg transition-colors",
            index === 0 ? "bg-yellow-50 border border-yellow-200" : "hover:bg-gray-50",
            onViewAgentDetails && "cursor-pointer"
          )}
          onClick={() => handleAgentClick(agent)}
        >
          {/* Rank Badge */}
          {getRankBadge(index + 1)}

          {/* Agent Avatar */}
          <Avatar className="h-10 w-10">
            <AvatarImage src={undefined} alt={agent.name} />
            <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
              {agent.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </AvatarFallback>
          </Avatar>

          {/* Agent Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm truncate">{agent.name}</p>
              {index === 0 && (
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              )}
              {agent.isOnline && (
                <span className="w-2 h-2 rounded-full bg-green-500" title="Online" />
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {agent.specialization || "Life Insurance Agent"}
            </p>
            {agent.agencyName && (
              <p className="text-xs text-primary truncate">
                {agent.agencyName}
              </p>
            )}
          </div>

          {/* Stats */}
          {/* Two premium figures side by side rather than one bracketed inside
              the other — "$30,430 ($14,607 issued)" read as a single number to
              anyone skimming, and nothing said which was which. Each column now
              carries its own label. */}
          <div className="perf-stats flex items-center gap-5 text-right sm:gap-6">
            <div className="min-w-[86px]">
              {/* div, not p — TooltipContent renders a div and nesting one in a
                  p is invalid HTML (hydration error). */}
              <div className="text-sm font-semibold text-emerald-600">
                ${Math.round(agent.issuedPremium || 0).toLocaleString()}
              </div>
              <div className="flex items-center justify-end gap-1 whitespace-nowrap text-xs text-muted-foreground">
                Premium Sold
                <TooltipProvider>
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <button type="button" aria-label="What counts as premium sold" className="inline-flex cursor-help items-center align-middle">
                        <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-64 text-left text-xs font-normal leading-snug text-popover-foreground">
                      Only policies marked sold and approved.
                    </TooltipContent>
                  </UITooltip>
                </TooltipProvider>
              </div>
            </div>
            <div className="min-w-[86px]">
              <div className="text-sm font-semibold">
                ${Math.round(agent.totalPremium || 0).toLocaleString()}
              </div>
              <div className="flex items-center justify-end gap-1 whitespace-nowrap text-xs text-muted-foreground">
                Total Production
                <TooltipProvider>
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <button type="button" aria-label="What counts as total production" className="inline-flex cursor-help items-center align-middle">
                        <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-64 text-left text-xs font-normal leading-snug text-popover-foreground">
                      Everything marked sold and approved as well as in underwriting.
                    </TooltipContent>
                  </UITooltip>
                </TooltipProvider>
              </div>
            </div>
            {/* One number, not "11 / 20" — a slash between two counts reads as
                a ratio or a fraction. The total lives in the tooltip, where it
                answers the follow-up question without competing for the glance. */}
            <div className="min-w-[70px]">
              <p className="text-sm font-semibold text-emerald-600">
                {agent.issuedPolicies ?? 0}
              </p>
              <div className="flex items-center justify-end gap-1 whitespace-nowrap text-xs text-muted-foreground">
                Policies Sold
                <TooltipProvider>
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <button type="button" aria-label="How policies are counted" className="inline-flex cursor-help items-center align-middle">
                        <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-64 text-left text-xs font-normal leading-snug text-popover-foreground">
                      Policies marked sold and approved. This agent has{" "}
                      {(agent.totalPolicies || 0).toLocaleString()} in total, counting those
                      still in underwriting.
                    </TooltipContent>
                  </UITooltip>
                </TooltipProvider>
              </div>
            </div>
            <div className="min-w-[52px]">
              <p className="text-sm font-medium">
                {(agent.closingRate || 0).toFixed(1)}%
              </p>
              <p className="whitespace-nowrap text-xs text-muted-foreground">Close Rate</p>
            </div>
            {/* View Details Icon */}
            {onViewAgentDetails && (
              <div className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-primary/10 transition-colors">
                <Eye className="h-4 w-4 text-primary" />
              </div>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );

  // Determine which content to show
  const content = isLoading
    ? loadingContent
    : error
    ? errorContent
    : topPerformers.length === 0
    ? emptyContent
    : performersContent;

  // When in a tab, render without Card wrapper
  if (inTab) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle data-tour="team-leaderboard" className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-500" />
          Top Performers
        </CardTitle>
        <CardDescription>Best performing agents across all agencies you own</CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
};
